import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CrmService } from '../../crm.service';
import { CRM_REGISTRATION_ATTACHMENT_TYPES, CrmClientRegistrationAttachment, CrmLead, CrmNote, CrmCommunicationLog, CrmReminder, CrmActivityItem, CrmActivityType, CrmLeadConversionRequest, CrmLeadConversionResult, CrmRoleOption, crmRoleLabel } from '../../crm.model';
import { ToolbarService } from '../../../services/toolbar.service';
import { AbstractControl, FormsModule, ReactiveFormsModule, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { PartyService } from '../../../party/party.service';
import { Party } from '../../../party/party.model';
import { Company } from '../../../company/company.model';
import { Customer } from '../../../customer/customer.model';
import { Country } from '../../../models/common/country.model';
import { City } from '../../../models/common/city.model';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { BaseMasterDataService } from '../../../services/base-master-data.service';
import { SessionStorageService } from '../../../services/session-storage.service';

// PrimeNG
import { TabsModule } from 'primeng/tabs';
import { TimelineModule } from 'primeng/timeline';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DividerModule } from 'primeng/divider';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TextareaModule } from 'primeng/textarea';
import { DialogModule } from 'primeng/dialog';
import { MenuItem, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { gccCrNumberValidator, gccRegistrationError, gccVatNumberValidator } from '../../crm-gcc-registration.validation';

@Component({
  selector: 'app-lead-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TabsModule,
    TimelineModule,
    TableModule,
    CardModule,
    ButtonModule,
    TagModule,
    BreadcrumbModule,
    DividerModule,
    FormsModule,
    ReactiveFormsModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    MultiSelectModule,
    TextareaModule,
    DialogModule,
    DatePickerModule,
    RadioButtonModule,
    ProgressSpinnerModule,
    ToastModule,
    SelectModule,
    PageShellComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent
  ],
  providers: [MessageService],
  templateUrl: './lead-details.component.html',
  styleUrls: ['./lead-details.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private crmService = inject(CrmService);
  private messageService = inject(MessageService);
  private partyService = inject(PartyService);
  private firebaseStorageService = inject(FirebaseStorageService);
  private baseMasterDataService = inject(BaseMasterDataService);
  private sessionStorageService = inject(SessionStorageService);
  public router = inject(Router);
  private fb = inject(FormBuilder);
  public toolbarService = inject(ToolbarService);

  public lead = signal<CrmLead | null>(null);
  public notes = signal<CrmNote[]>([]);
  public communications = signal<CrmCommunicationLog[]>([]);
  public reminders = signal<CrmReminder[]>([]);
  public activities = signal<CrmActivityItem[]>([]);
  public registrationAttachments = signal<CrmClientRegistrationAttachment[]>([]);
  public countries = signal<Country[]>([]);
  public cities = signal<City[]>([]);
  public crmRoles = signal<CrmRoleOption[]>([]);
  private loadedCityCountryIds = new Set<number>();
  private loadingCityCountryIds = new Set<number>();
  public showAttachmentDialog = signal(false);
  public uploadingAttachment = signal(false);
  public attachmentDraft = signal<Partial<CrmClientRegistrationAttachment>>({ category: 'VAT_CERTIFICATE' });
  public attachmentTypes = CRM_REGISTRATION_ATTACHMENT_TYPES;
  public metrics = computed<PageMetric[]>(() => {
    const activeLead = this.lead();
    return [
      { label: 'Status', value: activeLead?.status || 'NEW', tone: 'info' },
      { label: 'Quality', value: activeLead?.leadQuality || 'MEDIUM' },
      { label: 'Business Type', value: activeLead?.businessType || '-' },
      { label: 'Activities', value: this.activities().length, tone: 'success' }
    ];
  });

  public breadcrumbItems = signal<MenuItem[]>([]);
  public homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  public activeTabValue = 0;
  private readonly tabKeys = ['activity', 'notes', 'reminders', 'attachments'] as const;

  // Note Dialog
  public showNoteDialog = signal(false);
  public newNoteText = signal('');

  public conversionDialog = signal(false);
  public loadingConversion = signal(false);
  public companyOptions = signal<{ label: string; value: number; party: Party }[]>([]);
  public employeeOptions = signal<{ label: string; value: number; party: Party }[]>([]);
  public businessTypes = [
    { label: 'B2B', value: 'B2B' },
    { label: 'B2C', value: 'B2C' }
  ];
  public conversionForm = this.fb.group({
    existingCompanyPartyId: [null as number | null],
    companyName: ['', Validators.required],
    customerNameLocal: ['', Validators.required],
    postingDate: [new Date() as Date | string | null, Validators.required],
    currencyCode: ['SAR', Validators.required],
    exchangeRate: [1, Validators.required],
    businessType: ['B2B', Validators.required],
    natureOfBusiness: [''],
    crNumber: [null as number | null, [gccCrNumberValidator(() => this.selectedConversionCountry())]],
    taxRegNumber: ['', [gccVatNumberValidator(() => this.selectedConversionCountry())]],
    phoneNumber: [''],
    emailAddress: [''],
    websiteURL: [''],
    address: [''],
    postalCode: [''],
    countryId: [null as number | null, Validators.required],
    cityId: [null as number | null],
    contactName: ['', Validators.required],
    contactEmail: ['', [Validators.email]],
    contactPhoneNumber: [''],
    kamPartyId: [null as number | null],
    clientCategory: ['STANDARD'],
    roles: [[] as string[], Validators.required]
  }, { validators: [this.conversionContactMethodValidator] });

  // Reminder Dialog
  public showReminderDialog = signal(false);
  public newReminder = signal<CrmReminder>({
    dueDate: new Date(),
    reminderText: '',
    completed: false,
    assignedUserId: 0,
    companyId: 0
  });

  // Comm Log Dialog
  public showCommDialog = signal(false);
  public newCommLog = signal<CrmCommunicationLog>({
    type: 'CALL',
    communicationDate: new Date(),
    summary: '',
    status: 'COMPLETED',
    companyId: 0
  });

  updateCommType(type: string) {
    this.newCommLog.update(val => ({ ...val, type }));
  }
  updateCommSummary(summary: string) {
    this.newCommLog.update(val => ({ ...val, summary }));
  }
  updateCommDate(date: Date | null) {
    if (date) this.newCommLog.update(val => ({ ...val, communicationDate: date }));
  }
  updateReminderText(text: string) {
    this.newReminder.update(val => ({ ...val, reminderText: text }));
  }
  updateReminderDate(date: Date | null) {
    if (date) this.newReminder.update(val => ({ ...val, dueDate: date }));
  }

  ngOnInit() {
    const id = this.route.snapshot.params['id'];
    this.route.queryParamMap.subscribe(queryParams => {
      this.activeTabValue = this.resolveTabValue(queryParams.get('tab'));
    });
    this.loadCountries();
    this.loadRoles();
    this.conversionForm.get('countryId')?.valueChanges.subscribe(countryId => {
      this.conversionForm.get('crNumber')?.updateValueAndValidity({ emitEvent: false });
      this.conversionForm.get('taxRegNumber')?.updateValueAndValidity({ emitEvent: false });
      const currentCityId = this.conversionForm.get('cityId')?.value;
      if (!countryId || (currentCityId && !this.cityBelongsToCountry(currentCityId, countryId))) {
        this.conversionForm.patchValue({ cityId: null }, { emitEvent: false });
      }
      this.loadCitiesForCountry(countryId);
    });
    if (id) {
      this.loadLead(Number(id));
      this.loadTabData(Number(id));
      this.loadConversionLookups();
    }

    this.breadcrumbItems.set([
      { label: 'CRM', routerLink: '/crm' },
      { label: 'Leads', routerLink: '/crm/leads' },
      { label: 'Lead Details' }
    ]);
  }

  onTabChange(value: string | number) {
    this.activeTabValue = Number(value);
    const tabKey = this.tabKeys[this.activeTabValue];
    if (!tabKey) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabKey },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private resolveTabValue(tabKey: string | null): number {
    if (!tabKey) {
      return 0;
    }
    const tabIndex = this.tabKeys.findIndex(key => key === tabKey.toLowerCase());
    return tabIndex >= 0 ? tabIndex : 0;
  }

  loadLead(id: number) {
    this.crmService.getLead(id).subscribe(response => {
      if (response.success && response.data) {
        this.lead.set(response.data);
        if (this.route.snapshot.queryParamMap.get('action') === 'convert-to-client') {
          if (response.data.convertedClientId) {
            this.returnAfterClientConversion(response.data.convertedClientId);
          } else {
            queueMicrotask(() => this.openConvertDialog());
          }
        }
      }
    });
  }

  loadConversionLookups() {
    const companyId = this.toolbarService.customer()?.company?.id;
    if (!companyId) {
      return;
    }

    this.partyService.getParties(companyId, ['Company'], '', 0, 500).subscribe(page => {
      this.companyOptions.set((page.content || []).map(party => ({
        label: party.entityName,
        value: party.id.entityId,
        party
      })));
    });

    this.partyService.getParties(companyId, ['Employee'], '', 0, 500).subscribe(page => {
      this.employeeOptions.set((page.content || []).map(party => ({
        label: party.entityName,
        value: party.id.entityId,
        party
      })));
    });
  }

  loadCountries() {
    const cachedCountries = this.sessionStorageService.getCountryList() ?? [];
    if (cachedCountries.length) {
      this.countries.set(cachedCountries);
    }
    this.baseMasterDataService.getCountries('en-US').subscribe({
      next: countries => {
        if (countries?.length) this.countries.set(countries);
      }
    });
  }

  loadRoles() {
    this.crmService.getCrmRoles().subscribe({
      next: roles => this.crmRoles.set(roles || []),
      error: () => this.crmRoles.set([])
    });
  }

  loadTabData(id: number) {
    this.crmService.getLeadActivityTimeline(id).subscribe(data => this.activities.set(data));
    this.crmService.getNotesByLead(id).subscribe(data => this.notes.set(data));
    this.crmService.getCommunicationLogsByLead(id).subscribe(data => this.communications.set(data));
    this.crmService.getRemindersByLead(id).subscribe(data => this.reminders.set(data));
    this.crmService.getLeadRegistrationAttachments(id).subscribe(data => this.registrationAttachments.set(data || []));
  }

  saveNote() {
    const activeLead = this.lead();
    if (!activeLead) return;

    const note: CrmNote = {
      leadId: activeLead.id,
      text: this.newNoteText(),
      companyId: activeLead.companyId,
      createdByUserId: this.toolbarService.customer()?.id || 0
    };

    this.crmService.saveNote(note).subscribe(res => {
      if (res.success) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Note added' });
        this.showNoteDialog.set(false);
        this.newNoteText.set('');
        this.loadTabData(activeLead.id!);
      }
    });
  }

  saveReminder() {
    const activeLead = this.lead();
    if (!activeLead) return;

    const reminder = { ...this.newReminder(), leadId: activeLead.id, companyId: activeLead.companyId };
    reminder.assignedUserId = this.toolbarService.customer()?.id || 0;

    this.crmService.saveReminder(reminder).subscribe(res => {
      if (res.success) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Reminder set' });
        this.showReminderDialog.set(false);
        this.loadTabData(activeLead.id!);
      }
    });
  }

  saveCommLog() {
    const activeLead = this.lead();
    if (!activeLead) return;

    const log = { ...this.newCommLog(), leadId: activeLead.id, companyId: activeLead.companyId };
    log.createdByUserId = this.toolbarService.customer()?.id || 0;

    this.crmService.saveCommunicationLog(log).subscribe(res => {
      if (res.success) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Activity logged' });
        this.showCommDialog.set(false);
        this.loadTabData(activeLead.id!);
      }
    });
  }

  createQuotation() {
    const activeLead = this.lead();
    if (!activeLead?.id) return;
    this.router.navigate(['/crm/quotations/new'], {
      queryParams: {
        sourceType: 'LEAD',
        sourceId: activeLead.id
      }
    });
  }

  uploadRegistrationAttachment(event: Event) {
    const activeLead = this.lead();
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!activeLead?.id || !file) return;

    this.uploadingAttachment.set(true);
    this.firebaseStorageService.uploadFileAndGetPublicUrl(file, `crm/lead-registration/${activeLead.id}/${Date.now()}-${file.name}`).subscribe({
      next: fileUrl => {
        const attachment: CrmClientRegistrationAttachment = {
          category: this.attachmentDraft().category || 'VAT_CERTIFICATE',
          fileName: file.name,
          fileUrl,
          fileType: file.type,
          fileSize: file.size,
          uploadedByCustomerId: this.toolbarService.customer()?.id
        };
        this.crmService.saveLeadRegistrationAttachment(activeLead.id!, attachment, activeLead.companyId).subscribe(response => {
          this.uploadingAttachment.set(false);
          input.value = '';
          if (response.success) {
            this.showAttachmentDialog.set(false);
            this.attachmentDraft.set({ category: 'VAT_CERTIFICATE' });
            this.loadTabData(activeLead.id!);
          }
        });
      },
      error: () => {
        this.uploadingAttachment.set(false);
        input.value = '';
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload registration attachment.' });
      }
    });
  }

  deleteRegistrationAttachment(attachment: CrmClientRegistrationAttachment) {
    const activeLead = this.lead();
    if (!activeLead?.id || !attachment.id) return;
    this.crmService.deleteLeadRegistrationAttachment(activeLead.id, attachment.id, activeLead.companyId).subscribe(() => this.loadTabData(activeLead.id!));
  }

  registrationAttachmentLabel(category?: string): string {
    return this.attachmentTypes.find(type => type.value === category)?.label || category || '-';
  }

  updateAttachmentCategory(category: CrmClientRegistrationAttachment['category']) {
    this.attachmentDraft.update(value => ({ ...value, category }));
  }

  openConvertDialog() {
    const activeLead = this.lead();
    if (!activeLead) return;

    this.conversionForm.reset({
      existingCompanyPartyId: null,
      companyName: activeLead.companyName,
      customerNameLocal: activeLead.customerNameLocal || '',
      postingDate: new Date(),
      currencyCode: 'SAR',
      exchangeRate: 1,
      businessType: activeLead.businessType || 'B2B',
      natureOfBusiness: activeLead.natureOfBusiness || '',
      crNumber: activeLead.crNumber ?? null,
      taxRegNumber: activeLead.taxRegNumber || '',
      phoneNumber: activeLead.pocPhoneNumber || activeLead.mobileNumber || '',
      emailAddress: activeLead.pocEmail || '',
      websiteURL: activeLead.websiteURL || '',
      address: this.buildLeadAddress(activeLead),
      postalCode: activeLead.postalCode || '',
      countryId: activeLead.countryId ?? this.toolbarService.customer()?.company?.countryId ?? null,
      cityId: null,
      contactName: activeLead.pocName,
      contactEmail: activeLead.pocEmail,
      contactPhoneNumber: activeLead.mobileNumber || activeLead.pocPhoneNumber || '',
      kamPartyId: activeLead.kamCustomerId ?? null,
      clientCategory: 'STANDARD',
      roles: activeLead.roles || []
    }, { emitEvent: false });
    this.conversionForm.get('crNumber')?.updateValueAndValidity({ emitEvent: false });
    this.conversionForm.get('taxRegNumber')?.updateValueAndValidity({ emitEvent: false });
    this.loadCitiesForCountry(this.conversionForm.get('countryId')?.value, () => this.patchConversionCityFromName(activeLead.city));
    this.conversionDialog.set(true);
  }

  convertLeadToProspect() {
    if (this.loadingConversion()) {
      return;
    }
    const activeLead = this.lead();
    if (!activeLead?.id) return;
    this.loadingConversion.set(true);
    this.crmService.convertLeadToProspect(activeLead.id, this.toolbarService.customer()?.id, activeLead.companyId).subscribe({
      next: (response) => {
        this.loadingConversion.set(false);
        if (response.success && response.data?.id) {
          this.messageService.add({ severity: 'success', summary: 'Converted', detail: 'Lead converted to prospect successfully' });
          this.router.navigate(['/crm/prospects', response.data.id]);
        }
      },
      error: () => this.loadingConversion.set(false)
    });
  }

  onExistingCompanyChange(companyId: number | null) {
    const selected = this.companyOptions().find(option => option.value === companyId)?.party;
    if (!selected) return;

    this.conversionForm.patchValue({
      companyName: selected.entityName,
      customerNameLocal: '',
      crNumber: selected.crNumber ? Number(selected.crNumber) : null,
      taxRegNumber: selected.taxRegNumber ?? '',
      phoneNumber: selected.phoneNumber ?? '',
      emailAddress: selected.emailAddress ?? '',
      websiteURL: '',
      address: selected.address ?? '',
      postalCode: '',
      countryId: this.toolbarService.customer()?.company?.countryId ?? null,
      cityId: null
    });
  }

  convertLeadToClient() {
    if (this.loadingConversion()) {
      return;
    }
    const activeLead = this.lead();
    if (!activeLead?.id) return;

    const form = this.conversionForm.value;
    if (this.conversionForm.invalid) {
      this.conversionForm.markAllAsTouched();
      const detail = this.conversionValidationMessage();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail });
      return;
    }

    const address = this.buildConversionAddress(form.address ?? '', form.postalCode ?? '', form.cityId ?? null);
    const company = {
      id: form.existingCompanyPartyId ?? undefined,
      name: form.companyName,
      crNumber: form.crNumber ?? null,
      taxRegNumber: form.taxRegNumber ?? null,
      phoneNumber: form.phoneNumber ?? null,
      emailAddress: form.emailAddress ?? null,
      websiteURL: form.websiteURL ?? null,
      address,
      countryId: form.countryId,
      location: undefined as any,
      logo: null,
      placeId: null,
      poBox: null,
      defaultContactId: null,
      transportationAgencyId: null,
      freightForwarderId: null,
      enableEnterpriseFeatures: undefined
    } as any as Company;

    const customer = {
      id: undefined,
      firebaseId: `crm-${Date.now()}`,
      name: form.contactName,
      emailAddress: form.contactEmail,
      phoneNumber: form.contactPhoneNumber ?? '',
      imageUrl: null,
      dateOfBirth: undefined,
      gender: undefined,
      totalRating: undefined,
      isPhoneVerified: true,
      corporateRole: 'CONTACT',
      address,
      company,
      countryId: form.countryId
    } as any as Customer;

    const payload: CrmLeadConversionRequest = {
      company,
      customer,
      reuseCompany: !!form.existingCompanyPartyId,
      reuseCustomer: true,
      kamPartyId: form.kamPartyId ?? null,
      clientCategory: form.clientCategory ?? 'STANDARD',
      customerNameLocal: form.customerNameLocal ?? null,
      postingDate: form.postingDate ?? null,
      currencyCode: form.currencyCode ?? null,
      exchangeRate: form.exchangeRate ?? null,
      businessType: form.businessType ?? null,
      natureOfBusiness: form.natureOfBusiness ?? null,
      roles: form.roles || []
    };

    this.loadingConversion.set(true);
    this.crmService.convertLeadToClient(activeLead.id, payload).subscribe({
      next: (response) => {
        this.loadingConversion.set(false);
        if (response.success && response.data) {
          const result = response.data as CrmLeadConversionResult;
          this.lead.set(result.lead);
          this.conversionDialog.set(false);
          this.messageService.add({ severity: 'success', summary: 'Converted', detail: 'Lead converted to client successfully' });
          if (result.client?.id) {
            if (!this.returnAfterClientConversion(result.client.id)) {
              this.router.navigate(['/crm/clients', result.client.id]);
            }
          }
        }
      },
      error: error => {
        this.loadingConversion.set(false);
        const detail = error?.error?.message || error?.error?.detail || 'The lead could not be converted to a client.';
        this.messageService.add({ severity: 'error', summary: 'Conversion Failed', detail });
      }
    });
  }

  private buildLeadAddress(lead: CrmLead): string {
    return [lead.street1, lead.city, lead.postalCode]
      .filter(part => !!part && String(part).trim().length > 0)
      .join(', ');
  }

  private returnAfterClientConversion(clientId?: number) {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (!returnUrl) return false;
    const separator = returnUrl.includes('?') ? '&' : '?';
    this.router.navigateByUrl(`${returnUrl}${separator}convertedClientId=${clientId ?? ''}`);
    return true;
  }

  conversionCityOptions() {
    const countryId = this.conversionForm.get('countryId')?.value;
    if (!countryId) return [];
    return this.cities().filter(city => city.country?.id === countryId);
  }

  cityLabel(city?: City | number | null): string {
    if (typeof city === 'number') {
      city = this.cityById(city);
    }
    return city ? [city.cityName, city.country?.name].filter(Boolean).join(', ') : '';
  }

  private loadCitiesForCountry(countryId?: number | null, afterLoad?: () => void) {
    if (!countryId) {
      afterLoad?.();
      return;
    }
    if (this.loadedCityCountryIds.has(countryId)) {
      afterLoad?.();
      return;
    }
    if (this.loadingCityCountryIds.has(countryId)) return;

    this.loadingCityCountryIds.add(countryId);
    this.baseMasterDataService.getCities('en-US', countryId).subscribe({
      next: cities => {
        const existingById = new Map(this.cities().map(city => [city.id, city]));
        (cities ?? []).forEach(city => existingById.set(city.id, city));
        this.cities.set(Array.from(existingById.values()).sort((a, b) => (a.cityName || '').localeCompare(b.cityName || '')));
        this.loadedCityCountryIds.add(countryId);
        this.loadingCityCountryIds.delete(countryId);
        afterLoad?.();
      },
      error: () => {
        this.loadingCityCountryIds.delete(countryId);
        afterLoad?.();
      }
    });
  }

  private cityById(cityId?: number | null) {
    return this.cities().find(city => city.id === cityId);
  }

  private cityBelongsToCountry(cityId?: number | null, countryId?: number | null) {
    if (!cityId || !countryId) return false;
    return this.cityById(cityId)?.country?.id === countryId;
  }

  private patchConversionCityFromName(cityName?: string | null) {
    const normalizedCity = String(cityName || '').trim().toLowerCase();
    if (!normalizedCity || this.conversionForm.get('cityId')?.value) return;
    const matchedCity = this.conversionCityOptions().find(city => {
      const label = this.cityLabel(city).toLowerCase();
      const name = (city.cityName || '').trim().toLowerCase();
      return normalizedCity === name || normalizedCity === label || normalizedCity.startsWith(`${name},`);
    });
    if (matchedCity) {
      this.conversionForm.patchValue({ cityId: matchedCity.id }, { emitEvent: false });
    }
  }

  private buildConversionAddress(address: string, postalCode: string, cityId?: number | null): string {
    const city = this.cityById(cityId);
    const parts = [address, city?.cityName, postalCode]
      .map(part => String(part || '').trim())
      .filter(Boolean);
    const uniqueParts = parts.filter((part, index) => {
      const lowerPart = part.toLowerCase();
      return parts.findIndex(item => item.toLowerCase() === lowerPart || item.toLowerCase().includes(lowerPart)) === index;
    });
    return uniqueParts.join(', ');
  }

  private selectedConversionCountry(): Country | null {
    const countryId = this.conversionForm?.get('countryId')?.value;
    return this.countries().find(country => country.id === countryId) ?? null;
  }

  fieldError(controlName: string): string | null {
    const control = this.conversionForm.get(controlName);
    if (!control || (!control.touched && !control.dirty)) return null;
    if (control.hasError('required')) return controlName === 'roles' ? 'Select at least one business role.' : 'This field is required.';
    if (control.hasError('email')) return 'Enter a valid email address.';
    return gccRegistrationError(control);
  }

  private conversionValidationMessage(): string {
    if (this.conversionForm.hasError('contactMethod')) return 'Contact email or phone is required.';
    for (const controlName of ['companyName', 'customerNameLocal', 'postingDate', 'currencyCode', 'exchangeRate', 'businessType', 'countryId', 'contactName', 'contactEmail', 'crNumber', 'taxRegNumber', 'roles']) {
      const error = this.fieldError(controlName);
      if (error) return error;
    }
    return 'There are validation errors. Please review the highlighted fields.';
  }

  private conversionContactMethodValidator(control: AbstractControl): ValidationErrors | null {
    const email = String(control.get('contactEmail')?.value || '').trim();
    const phone = String(control.get('contactPhoneNumber')?.value || '').trim();
    return email || phone ? null : { contactMethod: true };
  }

  roleLabel(role?: string | null): string {
    return crmRoleLabel(role);
  }

  getStatusSeverity(status: string): "success" | "secondary" | "info" | "warn" | "danger" | "contrast" | undefined {
    switch (status.toLowerCase()) {
      case 'new': return 'info';
      case 'contacted': return 'warn';
      case 'qualified': return 'success';
      case 'lost': return 'danger';
      default: return 'secondary';
    }
  }

  getActivitySeverity(activityType: CrmActivityType) {
    switch (activityType) {
      case CrmActivityType.NOTE:
        return 'bg-blue-500';
      case CrmActivityType.REMINDER:
        return 'bg-orange-500';
      case CrmActivityType.COMMUNICATION:
      default:
        return 'bg-purple-500';
    }
  }

  getActivityIcon(activityType: CrmActivityType) {
    switch (activityType) {
      case CrmActivityType.NOTE:
        return 'pi pi-pencil';
      case CrmActivityType.REMINDER:
        return 'pi pi-calendar';
      case CrmActivityType.COMMUNICATION:
      default:
        return 'pi pi-comments';
    }
  }
}
